import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type ApiUser, type CreditPack, type ProgressSummary } from './api';
import { setSink, type TalesEvent } from './analytics';

export interface SessionState {
  ready: boolean;
  online: boolean;
  user: ApiUser | undefined;
  unlocks: Set<string>;
  progress: ProgressSummary[];
  packs: CreditPack[];
  setCredits: (n: number) => void;
  addUnlock: (seriesId: string, episodeId: string) => void;
  upsertProgress: (p: ProgressSummary) => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

/** Batches analytics events to the API (every 3 s, or immediately on hide/unload). */
function installEventSink() {
  let queue: (TalesEvent & { t: number; sid: string })[] = [];
  let timer: number | undefined;
  const flush = (beacon = false) => {
    if (!queue.length) return;
    const batch = queue;
    queue = [];
    api.sendEvents(batch, beacon);
  };
  setSink((e) => {
    queue.push(e);
    if (e.type === 'ending_reached' || e.type === 'choice_made' || e.type === 'play_start') flush();
    else {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => flush(), 3000);
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [online, setOnline] = useState(false);
  const [user, setUser] = useState<ApiUser | undefined>();
  const [unlocks, setUnlocks] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<ProgressSummary[]>([]);
  const [packs, setPacks] = useState<CreditPack[]>([]);

  const refresh = useCallback(async () => {
    const s = await api.startSession();
    if (s) {
      setUser(s.user);
      setUnlocks(new Set(s.unlocks));
      setProgress(s.progress);
      setPacks(s.packs);
      setOnline(true);
    } else setOnline(false);
    setReady(true);
  }, []);

  useEffect(() => {
    installEventSink();
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionState>(
    () => ({
      ready,
      online,
      user,
      unlocks,
      progress,
      packs,
      setCredits: (n) => setUser((u) => (u ? { ...u, credits: n } : u)),
      addUnlock: (s, e) => setUnlocks((prev) => new Set([...prev, `${s}/${e}`])),
      upsertProgress: (p) =>
        setProgress((prev) => {
          const i = prev.findIndex((x) => x.seriesId === p.seriesId && x.episodeId === p.episodeId);
          const next = [...prev];
          if (i >= 0) next[i] = p;
          else next.push(p);
          return next;
        }),
      refresh,
    }),
    [ready, online, user, unlocks, progress, packs, refresh],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside provider');
  return v;
}
